// store/modules/merchandising-collections.js
import API from "@/api/api-admin";
import API_V3 from "@/api/api-admin-v3";
import store from "@/store";

// 🆕 Normalisation pour API v2 : utilise status (pas state)
// eslint-disable-next-line no-unused-vars
const normalizeForApiV2 = (data) => {
  const normalized = { ...data };

  // API v2 utilise status (pas state)
  if (normalized.status && typeof normalized.status === "string") {
    normalized.status = normalized.status.toUpperCase();
  }
  // Support legacy : si state existe, le convertir en status
  if (normalized.state && typeof normalized.state === "string") {
    normalized.status = normalized.state.toUpperCase();
    delete normalized.state;
  }

  // Transformer mode en majuscules
  if (normalized.mode && typeof normalized.mode === "string") {
    normalized.mode = normalized.mode.toUpperCase();
  }

  return normalized;
};

// 🆕 Normalisation pour API v3 : utilise state (pas status)
const normalizeForApiV3 = (data) => {
  const normalized = { ...data };

  // API v3 utilise state (pas status)
  if (normalized.state && typeof normalized.state === "string") {
    normalized.state = normalized.state.toUpperCase();
  }
  // Support legacy : si status existe, le convertir en state
  if (normalized.status && typeof normalized.status === "string") {
    normalized.state = normalized.status.toUpperCase();
    delete normalized.status;
  }

  // Transformer mode en majuscules
  if (normalized.mode && typeof normalized.mode === "string") {
    normalized.mode = normalized.mode.toUpperCase();
  }

  return normalized;
};

// Fonction pour récupérer la locale du client depuis le sessionStorage
const getClientLocale = (brandId = null) => {
  try {
    const clientsCms = JSON.parse(sessionStorage.getItem("clientsCms") || "[]");

    // Essayer plusieurs sources pour le brandId
    let currentBrandId = brandId || store.state.user?.current_brand_id;

    // Si pas trouvé dans le store, essayer de l'extraire de l'URL
    if (!currentBrandId && typeof window !== "undefined") {
      const urlMatch = window.location.pathname.match(/\/brand\/(\d+)/);
      if (urlMatch) {
        currentBrandId = parseInt(urlMatch[1], 10);
      }
    }

    if (currentBrandId && Array.isArray(clientsCms)) {
      const brandIdAsNumber = parseInt(currentBrandId, 10);
      const currentClient = clientsCms.find(
        (client) => client.id === brandIdAsNumber
      );

      if (currentClient && currentClient.defaultLocaleCode) {
        return currentClient.defaultLocaleCode;
      }
    }

    return "fr_FR";
  } catch (error) {
    console.error(
      "🌍 Erreur lors de la récupération de la locale du client:",
      error
    );
    return "fr_FR";
  }
};

const state = {
  merchandisingCollections: [],
  merchandisingCollection: null,
  merchandisingCollectionsLoading: false,
  merchandisingCollectionsError: null,
  merchandisingCollectionsPagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  },

  // État pour la création/édition de collections
  collectionForm: {
    identification: {
      id_collection: "",
      title: "",
      description: "",
      state: "draft",
    },
    articles: {
      selectedIds: [],
      selectedArticlesData: [], // 🆕 Stockage des données complètes des articles
      selectedIdsByCollection: {}, // 🆕 Stockage des IDs par collection {brandId_collectionId: [ids]}
      selectedDataByCollection: {}, // 🆕 Stockage des données par collection {brandId_collectionId: [data]}
      mode: "auto", // 'auto' | 'manual'
      filters: [],
      sort: "stockDesc", // 'priceDesc' | 'priceAsc' | 'stockDesc' | 'stockAsc'
    },
    image: {
      file: null,
      base64: null,
      metadata: null,
    },
    ui: {
      currentStep: 1,
      loading: false,
      errors: {},
      hasUnsavedChanges: false,
    },
  },

  // 🆕 État pour l'édition de collections existantes
  editingCollection: {
    originalData: null,
    modifiedData: null,
    isEditing: false,
    editedArticlesData: [], // Données des articles pour les collections en édition
  },

  // 🆕 État pour la pagination des articles de collection
  collectionArticlesPagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20, // Par défaut 20 articles par page
  },

  // État pour la modale de construction de collection
  isBuildingCollection: false,
  buildingCollectionMessage: "",
};

const getters = {
  // 🆕 API v2 : Getter simplifié - les données sont déjà transformées dans l'action
  getMerchandisingCollections: (state) => state.merchandisingCollections,

  // 🆕 API v2 : Getter simplifié pour une collection unique
  getMerchandisingCollection: (state) => state.merchandisingCollection,
  isMerchandisingCollectionsLoading: (state) =>
    state.merchandisingCollectionsLoading,
  getMerchandisingCollectionsPagination: (state) =>
    state.merchandisingCollectionsPagination,
  getMerchandisingCollectionsError: (state) =>
    state.merchandisingCollectionsError,

  // Getters pour le formulaire de collection
  getCollectionForm: (state) => state.collectionForm,
  getCollectionFormIdentification: (state) =>
    state.collectionForm.identification,
  getCollectionFormArticles: (state) => state.collectionForm.articles,
  getCollectionFormSelectedIds: (state) =>
    state.collectionForm.articles.selectedIds,
  getCollectionFormSelectedArticlesData: (state) =>
    state.collectionForm.articles.selectedArticlesData,

  // 🆕 Getters pour récupérer les données par collection
  getSelectedIdsByCollection: (state) => (brandId, collectionId) => {
    const collectionKey = `${brandId}_${collectionId}`;
    return (
      state.collectionForm.articles.selectedIdsByCollection[collectionKey] || []
    );
  },

  getSelectedDataByCollection: (state) => (brandId, collectionId) => {
    const collectionKey = `${brandId}_${collectionId}`;
    return (
      state.collectionForm.articles.selectedDataByCollection[collectionKey] ||
      []
    );
  },
  getCollectionFormSort: (state) => state.collectionForm.articles.sort,
  getCollectionFormImage: (state) => state.collectionForm.image,
  getCollectionFormUI: (state) => state.collectionForm.ui,

  // 🆕 Getters calculés
  hasCollectionFormChanges: (state) => {
    return (
      state.collectionForm.ui.hasUnsavedChanges ||
      state.collectionForm.identification.title.length > 0 ||
      state.collectionForm.articles.selectedIds.length > 0 ||
      state.collectionForm.image.base64 !== null
    );
  },

  isCollectionFormValid: (state) => {
    const id = state.collectionForm.identification;
    return (
      id.id_collection.length > 0 &&
      id.title.length > 0 &&
      (state.collectionForm.articles.selectedIds.length > 0 ||
        state.collectionForm.articles.filters.length > 0)
    );
  },

  // 🆕 Getters pour l'édition
  getEditingCollection: (state) => state.editingCollection,
  isEditingCollection: (state) => state.editingCollection.isEditing,
  hasEditingChanges: (state) => {
    if (
      !state.editingCollection.originalData ||
      !state.editingCollection.modifiedData
    ) {
      return false;
    }
    return (
      JSON.stringify(state.editingCollection.originalData) !==
      JSON.stringify(state.editingCollection.modifiedData)
    );
  },

  // 🆕 Getters pour la pagination des articles de collection
  getCollectionArticlesPagination: (state) =>
    state.collectionArticlesPagination,
};

const mutations = {
  SET_MERCHANDISING_COLLECTIONS(state, collections) {
    state.merchandisingCollections = collections;
  },
  SET_MERCHANDISING_COLLECTION(state, collection) {
    state.merchandisingCollection = collection;
  },
  SET_MERCHANDISING_COLLECTIONS_LOADING(state, status) {
    state.merchandisingCollectionsLoading = status;
  },
  SET_MERCHANDISING_COLLECTIONS_ERROR(state, error) {
    state.merchandisingCollectionsError = error;
  },
  SET_MERCHANDISING_COLLECTIONS_PAGINATION(state, pagination) {
    state.merchandisingCollectionsPagination = {
      ...state.merchandisingCollectionsPagination,
      ...pagination,
    };
  },

  //  Mutations pour le formulaire de collection
  SET_COLLECTION_FORM_IDENTIFICATION(state, data) {
    state.collectionForm.identification = {
      ...state.collectionForm.identification,
      ...data,
    };
    state.collectionForm.ui.hasUnsavedChanges = true;
  },

  SET_COLLECTION_FORM_ARTICLES(state, data) {
    state.collectionForm.articles = {
      ...state.collectionForm.articles,
      ...data,
    };
    state.collectionForm.ui.hasUnsavedChanges = true;
  },

  SET_COLLECTION_FORM_SELECTED_ARTICLES(state, selectedIds) {
    state.collectionForm.articles.selectedIds = [...selectedIds];
    state.collectionForm.ui.hasUnsavedChanges = true;
  },

  SET_COLLECTION_FORM_SELECTED_ARTICLES_DATA(state, articlesData) {
    state.collectionForm.articles.selectedArticlesData = [...articlesData];
    state.collectionForm.ui.hasUnsavedChanges = true;
  },

  SET_COLLECTION_FORM_MODE(state, mode) {
    state.collectionForm.articles.mode = mode;
    state.collectionForm.ui.hasUnsavedChanges = true;
  },

  SET_COLLECTION_FORM_FILTERS(state, filters) {
    state.collectionForm.articles.filters = [...filters];
    state.collectionForm.ui.hasUnsavedChanges = true;
  },

  SET_COLLECTION_FORM_SORT(state, sort) {
    state.collectionForm.articles.sort = sort;
    state.collectionForm.ui.hasUnsavedChanges = true;
  },

  SET_COLLECTION_FORM_IMAGE(state, imageData) {
    state.collectionForm.image = {
      ...state.collectionForm.image,
      ...imageData,
    };
    state.collectionForm.ui.hasUnsavedChanges = true;
  },

  SET_COLLECTION_FORM_UI(state, uiData) {
    state.collectionForm.ui = { ...state.collectionForm.ui, ...uiData };
  },

  SET_COLLECTION_FORM_ERRORS(state, errors) {
    state.collectionForm.ui.errors = { ...errors };
  },

  // Mutation pour réinitialiser le formulaire
  RESET_COLLECTION_FORM(state) {
    state.collectionForm = {
      identification: {
        id_collection: "",
        title: "",
        description: "",
        state: "draft",
      },
      articles: {
        selectedIds: [],
        selectedArticlesData: [], //  Réinitialiser aussi les données des articles
        mode: "auto",
        filters: [],
        sort: "stockDesc",
      },
      image: {
        file: null,
        base64: null,
        metadata: null,
      },
      ui: {
        currentStep: 1,
        loading: false,
        errors: {},
        hasUnsavedChanges: false,
      },
    };
  },

  // 🆕 Mutations pour l'édition
  SET_EDITING_COLLECTION(state, { originalData, modifiedData = null }) {
    state.editingCollection = {
      originalData: originalData ? { ...originalData } : null,
      modifiedData: modifiedData
        ? { ...modifiedData }
        : originalData
        ? { ...originalData }
        : null,
      isEditing: true,
      editedArticlesData: [], // Réinitialiser les données des articles
    };
  },

  UPDATE_EDITING_COLLECTION(state, modifiedData) {
    if (state.editingCollection.isEditing) {
      state.editingCollection.modifiedData = { ...modifiedData };
    }
  },

  RESET_EDITING_COLLECTION(state) {
    state.editingCollection = {
      originalData: null,
      modifiedData: null,
      isEditing: false,
      editedArticlesData: [],
    };
  },

  // Nouvelle mutation pour gérer les données des articles en édition
  SET_EDITED_ARTICLES_DATA(state, articlesData) {
    state.editingCollection.editedArticlesData = articlesData || [];
  },

  // 🆕 Mutations pour gérer les données par collection
  SET_SELECTED_IDS_BY_COLLECTION(state, { collectionKey, ids }) {
    state.collectionForm.articles.selectedIdsByCollection = {
      ...state.collectionForm.articles.selectedIdsByCollection,
      [collectionKey]: ids || [],
    };
  },

  SET_SELECTED_DATA_BY_COLLECTION(state, { collectionKey, data }) {
    state.collectionForm.articles.selectedDataByCollection = {
      ...state.collectionForm.articles.selectedDataByCollection,
      [collectionKey]: data || [],
    };
  },

  // 🆕 Mutation pour la pagination des articles de collection
  SET_COLLECTION_ARTICLES_PAGINATION(state, pagination) {
    state.collectionArticlesPagination = {
      ...state.collectionArticlesPagination,
      ...pagination,
    };
  },

  // Mutations pour la modale de construction de collection
  SET_BUILDING_COLLECTION_MODAL(state, isOpen) {
    state.isBuildingCollection = isOpen;
  },
  SET_BUILDING_COLLECTION_MESSAGE(state, message) {
    state.buildingCollectionMessage = message;
  },
};

const actions = {
  // 🆕 Actions pour le formulaire de collection
  initializeCollectionForm({ commit }, initialData = {}) {
    commit("RESET_COLLECTION_FORM");
    if (Object.keys(initialData).length > 0) {
      if (initialData.identification) {
        commit(
          "SET_COLLECTION_FORM_IDENTIFICATION",
          initialData.identification
        );
      }
      if (initialData.articles) {
        commit("SET_COLLECTION_FORM_ARTICLES", initialData.articles);
      }
      if (initialData.image) {
        commit("SET_COLLECTION_FORM_IMAGE", initialData.image);
      }
      if (initialData.ui) {
        commit("SET_COLLECTION_FORM_UI", initialData.ui);
      }
    }
  },

  updateCollectionFormIdentification({ commit }, data) {
    commit("SET_COLLECTION_FORM_IDENTIFICATION", data);
  },

  updateCollectionFormArticles({ commit }, data) {
    commit("SET_COLLECTION_FORM_ARTICLES", data);
  },

  updateCollectionFormSelectedArticles({ commit }, selectedIds) {
    commit("SET_COLLECTION_FORM_SELECTED_ARTICLES", selectedIds);
  },

  updateCollectionFormSelectedArticlesData({ commit }, payload) {
    // Support ancien format (articlesData directement) et nouveau format (objet avec options)
    const articlesData = Array.isArray(payload) ? payload : payload.articlesData;
    const isPreviewMode = payload.isPreviewMode || false;

    // Si mode preview (création), limiter à 25 produits pour l'affichage
    const dataToStore = isPreviewMode ? articlesData.slice(0, 25) : articlesData;
    commit("SET_COLLECTION_FORM_SELECTED_ARTICLES_DATA", dataToStore);
  },

  // Nettoyer le mode édition après un PATCH immédiat
  clearEditingMode({ commit }) {
    commit("SET_COLLECTION_FORM_SELECTED_ARTICLES", []);
    commit("SET_COLLECTION_FORM_SELECTED_ARTICLES_DATA", []);
  },

  updateCollectionFormMode({ commit }, mode) {
    commit("SET_COLLECTION_FORM_MODE", mode);
  },

  updateCollectionFormFilters({ commit }, filters) {
    commit("SET_COLLECTION_FORM_FILTERS", filters);
  },

  updateCollectionFormSort({ commit }, sort) {
    commit("SET_COLLECTION_FORM_SORT", sort);
  },

  updateCollectionFormImage({ commit }, imageData) {
    commit("SET_COLLECTION_FORM_IMAGE", imageData);
  },

  updateCollectionFormUI({ commit }, uiData) {
    commit("SET_COLLECTION_FORM_UI", uiData);
  },

  setCollectionFormErrors({ commit }, errors) {
    commit("SET_COLLECTION_FORM_ERRORS", errors);
  },

  clearCollectionFormErrors({ commit }) {
    commit("SET_COLLECTION_FORM_ERRORS", {});
  },

  resetCollectionForm({ commit }) {
    commit("RESET_COLLECTION_FORM");
  },

  // 🆕 Actions pour l'édition
  startEditingCollection({ commit }, collectionData) {
    commit("SET_EDITING_COLLECTION", { originalData: collectionData });
  },

  updateEditingCollection({ commit }, modifiedData) {
    commit("UPDATE_EDITING_COLLECTION", modifiedData);
  },

  resetEditingCollection({ commit }) {
    commit("RESET_EDITING_COLLECTION");
  },

  // Nouvelle action pour gérer les données des articles en édition
  setEditedArticlesData({ commit }, articlesData) {
    commit("SET_EDITED_ARTICLES_DATA", articlesData);
  },

  // Action pour sauvegarder les données d'articles d'une collection (remplace localStorage)
  updateSelectedArticlesData({ commit }, { brandId, collectionId, data }) {
    const collectionKey = `${brandId}_${collectionId}`;
    commit("SET_SELECTED_DATA_BY_COLLECTION", { collectionKey, data });
  },

  //  Actions pour gérer les données par collection
  updateSelectedIdsByCollection({ commit }, { brandId, collectionId, ids }) {
    const collectionKey = `${brandId}_${collectionId}`;
    commit("SET_SELECTED_IDS_BY_COLLECTION", { collectionKey, ids });
  },

  //  Action utilitaire pour sauvegarder le formulaire depuis/vers localStorage (migration progressive)
  loadCollectionFormFromLocalStorage({ commit }) {
    try {
      const savedData = localStorage.getItem("collectionCreateState");
      if (savedData) {
        const parsed = JSON.parse(savedData);
        commit(
          "SET_COLLECTION_FORM_IDENTIFICATION",
          parsed.identification || {}
        );
        commit("SET_COLLECTION_FORM_ARTICLES", parsed.articles || {});
        commit("SET_COLLECTION_FORM_IMAGE", parsed.image || {});
        commit("SET_COLLECTION_FORM_UI", { hasUnsavedChanges: false });
        return true;
      }
    } catch (error) {
      console.warn("Erreur lors du chargement depuis localStorage:", error);
    }
    return false;
  },

  saveCollectionFormToLocalStorage({ state }) {
    try {
      const dataToSave = {
        identification: state.collectionForm.identification,
        articles: state.collectionForm.articles,
        image: {
          base64: state.collectionForm.image.base64,
          metadata: state.collectionForm.image.metadata,
        },
        timestamp: Date.now(),
      };
      localStorage.setItem("collectionCreateState", JSON.stringify(dataToSave));
      return true;
    } catch (error) {
      console.warn("Erreur lors de la sauvegarde vers localStorage:", error);
      return false;
    }
  },

  // Action pour créer une collection depuis le formulaire
  async createCollectionFromForm({ state, dispatch, commit }, { brandId }) {
    try {
      commit("SET_COLLECTION_FORM_UI", { loading: true });

      const formData = state.collectionForm;

      //  API v3 : Préparer les données pour l'API avec normalizeForApiV3
      const collectionData = normalizeForApiV3({
        brandId: parseInt(brandId, 10),
        newIds: formData.articles.selectedIds,
        state: formData.identification.state,
        mode: formData.articles.mode,
        filters: formData.articles.filters,
        sort: formData.articles.sort || "stockDesc",
        metadata: {
          fr_FR: {
            id_collection: formData.identification.id_collection,
            title: formData.identification.title,
            description: formData.identification.description,
          },
        },
      });

      // Créer la collection via l'action existante
      const result = await dispatch("createMerchandisingCollection", {
        brandId,
        collectionData,
      });

      // Si succès, réinitialiser le formulaire
      if (result) {
        commit("RESET_COLLECTION_FORM");
        // Nettoyer localStorage (selectedCollectionArticlesData et selectedCollectionArticleIds
        // ne sont plus dans localStorage car ils sont maintenant dans le store Vuex)
        localStorage.removeItem("collectionCreateState");
        localStorage.removeItem("selectedCollectionArticles");
        localStorage.removeItem("collectionMode");
        localStorage.removeItem("collectionImageBase64");
      }

      return result;
    } catch (error) {
      commit("SET_COLLECTION_FORM_ERRORS", { general: error.message });
      throw error;
    } finally {
      commit("SET_COLLECTION_FORM_UI", { loading: false });
    }
  },

  // Récupérer la liste des collections (API v2)
  async fetchMerchandisingCollections({ commit }, params = {}) {
    try {
      commit("SET_MERCHANDISING_COLLECTIONS_LOADING", true);

      const brandId = params.brandId;

      // URL basée sur le fichier call-api.md - Endpoint v2
      const url = `collection-pages/brand/${brandId}`;

      const response = await API.get(url, {
        headers: {
          "X-BRAND-ID": brandId,
        },
        params: {
          page:
            params.page || state.merchandisingCollectionsPagination.currentPage,
          limit:
            params.itemsPerPage ||
            state.merchandisingCollectionsPagination.itemsPerPage,
          locale: getClientLocale(brandId),
          ...params.filters,
        },
      });

      // Transformation des données pour correspondre à notre format
      let collections = [];
      let rawCollections = [];

      //  Gérer la nouvelle structure de l'API avec items et pagination
      if (
        response.data &&
        response.data.items &&
        Array.isArray(response.data.items)
      ) {
        // Nouvelle structure : { items: [...], pagination: {...} }
        rawCollections = response.data.items;
      } else if (response.data && Array.isArray(response.data)) {
        // Ancienne structure : tableau direct
        rawCollections = response.data;
      }

      if (rawCollections.length > 0) {
        collections = rawCollections.map((collectionRaw) => {
          // 🆕 API v2 : pas de normalisation, utiliser les données directement
          const collection = collectionRaw;

          // Utiliser la locale du client depuis le sessionStorage
          const currentLocale = getClientLocale(brandId);
          // Fallback vers une locale par défaut si la locale actuelle n'existe pas
          const fallbackLocale = "fr_FR";

          // 🆕 API v2 : mode est déjà en majuscules (AUTO/MANUAL)
          const fillTypeFromMode =
            collection.mode === "AUTO" ? "auto" : "manual";

          return {
            id: collection.id,
            // 🆕 API v2 : title et titleOnline sont maintenant des propriétés directes
            title: collection.title || "-",
            description:
              collection.metadata?.[currentLocale]?.description ||
              collection.metadata?.[fallbackLocale]?.description ||
              collection.metadata?.fr_FR?.description ||
              collection.metadata?.en_GB?.description ||
              "-",
            title_online: collection.titleOnline || "-",
            // 🆕 API v2 : gérer à la fois status (liste) et state (détails)
            status:
              collection.status === "COMPLETED" ||
              collection.status === "COMPLETE" ||
              collection.state === "COMPLETED" ||
              collection.state === "COMPLETE"
                ? "completed"
                : "draft",
            fillType: fillTypeFromMode,
            sort: collection.sort || "manual",
            //  API v2 : utiliser countItems au lieu de articleEan.length
            articles: collection.countItems || 0,
            creationDate: collection.createdAt
              ? new Date(collection.createdAt).toLocaleDateString()
              : "-",
            // API v2 : utiliser collectionId directement
            id_collection: collection.collectionId || "-",
            // API v2 : `discounts` est un tableau (plusieurs réductions actives sur une collection)
            discounts: Array.isArray(collection.discounts)
              ? collection.discounts.map((d) => ({
                  id: d.id,
                  name: d.name,
                  status: d.status,
                  isDiscountBadgeEnabled: d.isDiscountBadgeEnabled,
                  isStoppingInProgress: d.isStoppingInProgress,
                }))
              : [],
            // Pour la recherche BaseTable (qui stringifie un seul niveau) :
            // exposer les noms aplatis en virgule.
            discountsNames: Array.isArray(collection.discounts)
              ? collection.discounts.map((d) => d.name).join(", ")
              : "",
            hasStatusInfo: true,
            hasFillTypeInfo: true,
            rawData: collectionRaw, // Garder les données originales
          };
        });
      }

      commit("SET_MERCHANDISING_COLLECTIONS", collections);

      //  Priorité à response.data.pagination (nouvelle structure)
      if (response.data && response.data.pagination) {
        const pagination = response.data.pagination;
        commit("SET_MERCHANDISING_COLLECTIONS_PAGINATION", {
          currentPage: pagination.current || 1,
          totalPages: pagination.pageCount || 1,
          totalItems: pagination.totalCount || 0,
          itemsPerPage:
            params.itemsPerPage ||
            state.merchandisingCollectionsPagination.itemsPerPage,
        });
      }
      // Fallback : Mise à jour de la pagination si disponible dans les headers
      else if (response.headers && response.headers["x-total-count"]) {
        const totalItems = parseInt(response.headers["x-total-count"]);
        const totalPages = Math.ceil(
          totalItems /
            (params.itemsPerPage ||
              state.merchandisingCollectionsPagination.itemsPerPage)
        );

        commit("SET_MERCHANDISING_COLLECTIONS_PAGINATION", {
          currentPage:
            params.page || state.merchandisingCollectionsPagination.currentPage,
          totalPages,
          totalItems,
          itemsPerPage:
            params.itemsPerPage ||
            state.merchandisingCollectionsPagination.itemsPerPage,
        });
      }

      commit("SET_MERCHANDISING_COLLECTIONS_ERROR", null);
      return collections;
    } catch (error) {
      console.error("Error fetching collections:", error);
      commit(
        "SET_MERCHANDISING_COLLECTIONS_ERROR",
        error.message || "Failed to fetch collections"
      );
      return [];
    } finally {
      commit("SET_MERCHANDISING_COLLECTIONS_LOADING", false);
    }
  },

  // Récupérer une collection par son ID (API v3)
  async fetchMerchandisingCollectionById(
    { commit },
    { brandId, collectionId }
  ) {
    try {
      commit("SET_MERCHANDISING_COLLECTIONS_LOADING", true);

      // URL basée sur le fichier call-api.md - Endpoint v3
      const url = `collection-pages/${collectionId}`;

      const response = await API_V3.get(url, {
        headers: {
          "X-BRAND-ID": brandId,
        },
      });

      if (!response.data) {
        throw new Error("No data returned from API");
      }

      // Extraire les données de la collection
      // La réponse peut contenir une propriété "collection" ou les données directement
      const collectionData = response.data.collection || response.data;

      // API v3 : pas de normalisation, utiliser les données directement
      // Transformer les données
      // Utiliser la locale du client depuis le sessionStorage
      const currentLocale = getClientLocale(brandId);
      // Fallback vers une locale par défaut si la locale actuelle n'existe pas
      const fallbackLocale = "fr_FR";

      const collection = {
        id: collectionData.id,
        title:
          collectionData.metadata?.[currentLocale]?.title ||
          collectionData.metadata?.[fallbackLocale]?.title ||
          collectionData.metadata?.fr_FR?.title ||
          collectionData.metadata?.en_GB?.title ||
          "-",
        description:
          collectionData.metadata?.[currentLocale]?.description ||
          collectionData.metadata?.[fallbackLocale]?.description ||
          collectionData.metadata?.fr_FR?.description ||
          collectionData.metadata?.en_GB?.description ||
          "-",
        //  API v3 : utilise state (pas status) et mode en majuscules (COMPLETED/DRAFT, AUTO/MANUAL)
        status: (collectionData.state === "COMPLETE" || collectionData.state === "COMPLETED") ? "completed" : "draft",
        fillType: collectionData.mode === "AUTO" ? "auto" : "manual",
        articles: collectionData.articleEan
          ? collectionData.articleEan.length
          : 0,
        creationDate: collectionData.createdAt
          ? new Date(collectionData.createdAt).toLocaleDateString()
          : "-",
        // NOTE: ne semble pas consommé mais intégration du fallback aussi ici par cohérence
        filters: collectionData.filtersV2 || collectionData.filters || [],
        metadata: collectionData.metadata || {},
        rawData: collectionData, // Stocker les données originales
      };

      commit("SET_MERCHANDISING_COLLECTION", collection);
      commit("SET_MERCHANDISING_COLLECTIONS_ERROR", null);
      return collection;
    } catch (error) {
      console.error("Error fetching collection:", error);
      commit(
        "SET_MERCHANDISING_COLLECTIONS_ERROR",
        error.message || "Failed to fetch collection details"
      );
      return null;
    } finally {
      commit("SET_MERCHANDISING_COLLECTIONS_LOADING", false);
    }
  },

  // Récupérer les articles d'une collection (API v2)
  // silent: true pour éviter le loader global (utilisé pour la recherche/pagination)
  async fetchMerchandisingCollectionArticles(
    { commit },
    { brandId, collectionId, params = {}, silent = false }
  ) {
    try {
      if (!silent) {
        commit("SET_MERCHANDISING_COLLECTIONS_LOADING", true);
      }

      // Vérifier que brandId est défini avant d'appeler l'API
      if (!brandId) {
        console.error("Error: brandId is undefined");
        throw new Error("brandId is required");
      }

      //  Vérifier que collectionId est défini ET valide
      if (
        !collectionId ||
        collectionId === "undefined" ||
        collectionId === "null"
      ) {
        throw new Error("collectionId is required and must be valid");
      }

      // URL basée sur le fichier call-api.md - Endpoint v2
      const url = `brand/${brandId}/collection/${collectionId}/articles`;

      // Combiner les paramètres par défaut avec les paramètres personnalisés
      const requestParams = {
        locale: getClientLocale(brandId),
        ...params, // Inclure les paramètres additionnels comme sort
      };

      const response = await API.get(url, {
        headers: {
          "X-BRAND-ID": brandId,
          "Content-Type": "application/ld+json",
        },
        params: requestParams,
      });

      //  L'API v2 retourne { collection: {...}, articles: { items: [...], pagination: {...} }, discount: {...} | null }
      let result = {};

      // Extraire la collection depuis response.data.collection
      if (response.data.collection) {
        result.collection = response.data.collection;
      }

      const discountsList = response.data.discounts || [];
      result.discount =
        discountsList.find((d) => d.status !== "ended") || null;

      // Extraire les articles
      let articlesData = response.data;
      if (response.data.articles && response.data.articles.items) {
        // Nouvelle structure : { articles: { items: [...], pagination: {...}, total: X } }
        articlesData = response.data.articles;
        result.items = articlesData.items || [];
        result.pagination = articlesData.pagination || null;
        // total dans articles.pagination.totalCount = nombre de résultats (peut être filtré par search)
        result.total = articlesData.pagination?.totalCount || articlesData.total || articlesData.items?.length || 0;
        // totalCollection à la racine = nombre total d'articles dans la collection (hors search)
        result.totalCollection = articlesData.total || result.total;
      } else if (Array.isArray(response.data)) {
        // Fallback : tableau direct (ancien format)
        result.items = response.data;
        result.total = response.data.length;
      } else if (response.data.items) {
        // Format intermédiaire : { items: [...] }
        result.items = response.data.items;
        result.total = response.data.total || response.data.items.length;
        result.pagination = response.data.pagination || null;
      }

      //  Gestion de la pagination des articles
      if (result.pagination) {
        const pagination = result.pagination;
        commit("SET_COLLECTION_ARTICLES_PAGINATION", {
          currentPage: pagination.current || 1,
          totalPages: pagination.pageCount || 1,
          totalItems: pagination.totalCount || result.total || 0,
          itemsPerPage: params.limit || params.itemsPerPage || 20,
        });
      } else if (params.page || params.limit || params.itemsPerPage) {
        // Fallback si pas de pagination dans la réponse mais paramètres fournis
        const total = result.total || (result.items ? result.items.length : 0);
        const itemsPerPage = params.limit || params.itemsPerPage || 20;
        commit("SET_COLLECTION_ARTICLES_PAGINATION", {
          currentPage: params.page || 1,
          totalPages: Math.ceil(total / itemsPerPage),
          totalItems: total,
          itemsPerPage: itemsPerPage,
        });
      }

      commit("SET_MERCHANDISING_COLLECTIONS_ERROR", null);
      return result;
    } catch (error) {
      console.error("Error fetching collection articles:", error);
      commit(
        "SET_MERCHANDISING_COLLECTIONS_ERROR",
        error.message || "Failed to fetch collection articles"
      );
      return { items: [], total: 0 };
    } finally {
      if (!silent) {
        commit("SET_MERCHANDISING_COLLECTIONS_LOADING", false);
      }
    }
  },

  // Récupérer les articles filtrés pour une collection (API v2)
  async fetchFilteredArticlesForCollection(
    { commit },
    { brandId, collectionId = null, params = {} }
  ) {
    try {
      commit("SET_MERCHANDISING_COLLECTIONS_LOADING", true);

      // Vérifier que brandId est défini avant d'appeler l'API
      if (!brandId) {
        console.error("Error: brandId is undefined");
        throw new Error("brandId is required");
      }

      // Construction de l'URL
      let url;
      if (collectionId) {
        // URL pour les articles d'une collection spécifique
        url = `brand/${brandId}/collection-pages/${collectionId}/articles-filtered`;
      } else {
        // URL pour tous les articles filtrés
        url = `brand/${brandId}/collection-pages/articles-filtered`;
      }

      // Préparer les paramètres pour l'API
      const apiParams = {
        ...params,
        locale: getClientLocale(brandId),
      };

      const response = await API.get(url, {
        headers: {
          "X-BRAND-ID": brandId,
          "Content-Type": "application/ld+json",
        },
        params: apiParams,
      });

      // Traiter la réponse avec pagination
      if (response.data && response.data.items) {
        const items = response.data.items || [];
        const totalItems = response.data.total || items.length;
        const pagination = response.data.pagination || {};

        // Retourner les données avec pagination
        const formattedResponse = {
          items: items,
          total: totalItems,
          nextValuesFilter: response.data.nextValuesFilter || {},
          pagination: {
            current: pagination.current || apiParams.page || 1,
            pageCount:
              pagination.pageCount ||
              Math.ceil(totalItems / (apiParams.limit || 20)),
            totalCount: pagination.totalCount || totalItems,
          },
        };

        commit("SET_MERCHANDISING_COLLECTIONS_ERROR", null);
        return formattedResponse;
      }

      // Si format inattendu, renvoyer un objet vide formaté
      commit("SET_MERCHANDISING_COLLECTIONS_ERROR", null);
      return {
        items: [],
        total: 0,
        nextValuesFilter: {},
        pagination: {
          current: 1,
          pageCount: 1,
          totalCount: 0,
        },
      };
    } catch (error) {
      console.error("Error fetching filtered articles:", error);
      commit(
        "SET_MERCHANDISING_COLLECTIONS_ERROR",
        error.message || "Échec du chargement des articles filtrés"
      );
      return {
        items: [],
        total: 0,
        nextValuesFilter: {},
        pagination: {
          current: 1,
          pageCount: 1,
          totalCount: 0,
        },
      };
    } finally {
      commit("SET_MERCHANDISING_COLLECTIONS_LOADING", false);
    }
  },

  // Créer une nouvelle collection (API v3)
  async createMerchandisingCollection({ commit }, { brandId, collectionData }) {
    commit("SET_MERCHANDISING_COLLECTIONS_LOADING", true);
    try {
      const url = "collection-pages";

      // Préparer un tableau d'IDs valides
      let articleIds = [];

      // Si la propriété newIds existe, l'utiliser
      if (Array.isArray(collectionData.newIds)) {
        articleIds = collectionData.newIds;
      }
      // Support legacy : si articleId existe encore, l'utiliser
      else if (Array.isArray(collectionData.articleId)) {
        articleIds = collectionData.articleId;
      }
      // Si nous avons des EANs dans articleEans, les utiliser à la place
      else if (Array.isArray(collectionData.articleEans)) {
        articleIds = collectionData.articleEans;
      }

      // S'assurer que les filtres sont correctement formatés
      let filters = collectionData.filters || {};

      // Check if we have a CSV import file
      const importFile = collectionData.importFile;

      if (importFile) {
        // Defensive checks: the modal already validates the CSV content but a
        // direct dispatch from elsewhere could bypass it, so we re-guard size
        // and MIME here to avoid sending obviously invalid payloads.
        const MAX_IMPORT_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
        const ALLOWED_IMPORT_MIME_TYPES = [
          "text/csv",
          "application/csv",
          "application/vnd.ms-excel",
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "", // some browsers omit the type for .csv
        ];
        if (!(importFile instanceof File) && !(importFile instanceof Blob)) {
          throw new Error("Import file must be a File or Blob");
        }
        if (importFile.size === 0) {
          throw new Error("Import file is empty");
        }
        if (importFile.size > MAX_IMPORT_FILE_SIZE) {
          throw new Error(
            `Import file exceeds the ${MAX_IMPORT_FILE_SIZE / 1024 / 1024} MB limit`
          );
        }
        if (
          importFile.type &&
          !ALLOWED_IMPORT_MIME_TYPES.includes(importFile.type)
        ) {
          throw new Error(`Unsupported import file type: ${importFile.type}`);
        }

        // Multipart upload with CSV file
        const formData = new FormData();
        formData.append("importFile", importFile);
        formData.append("brandId", parseInt(brandId, 10));
        formData.append("origin", "import");
        if (collectionData.metadata) {
          formData.append("metadata", JSON.stringify(collectionData.metadata));
        }
        if (collectionData.sort) {
          formData.append("sort", collectionData.sort);
        }
        if (collectionData.state) {
          formData.append("state", collectionData.state);
        }
        if (collectionData.mode) {
          formData.append("mode", collectionData.mode);
        }
        if (collectionData.filters) {
          formData.append("filters", JSON.stringify(collectionData.filters));
        }

        const response = await API_V3.post(url, formData, {
          headers: {
            "X-Brand-ID": brandId,
            "Content-Type": "multipart/form-data",
          },
        });
        return response.data.collection || response.data;
      }

      // Standard JSON creation (no CSV)
      const payload = normalizeForApiV3({
        brandId: parseInt(brandId, 10),
        newIds: articleIds,
        state: collectionData.state,
        mode: collectionData.mode,
        filters: filters,
        sort: collectionData.sort || "stockDesc",
        metadata: collectionData.metadata || {},
      });

      const response = await API_V3.post(url, payload, {
        headers: {
          "X-Brand-ID": brandId,
          "Content-Type": "application/ld+json",
        },
      });
      return response.data.collection || response.data;
    } catch (error) {
      commit(
        "SET_MERCHANDISING_COLLECTIONS_ERROR",
        error.message || "Failed to create collection"
      );
      throw error;
    } finally {
      commit("SET_MERCHANDISING_COLLECTIONS_LOADING", false);
    }
  },

  // Mettre à jour une collection existante (API v3)

  async updateMerchandisingCollection(
    { commit },
    { collectionId, collectionData, silent = false }
  ) {
    try {
      if (!silent) {
        commit("SET_MERCHANDISING_COLLECTIONS_LOADING", true);
      }

      const url = `collection-pages/${collectionId}`;

      // If we have a CSV import file, use multipart
      if (collectionData.importFile) {
        const formData = new FormData();
        formData.append("importFile", collectionData.importFile);
        formData.append("origin", "import");
        if (collectionData.state) {
          formData.append("state", collectionData.state);
        }

        const response = await API_V3.patch(url, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        commit("SET_MERCHANDISING_COLLECTIONS_ERROR", null);
        return response.data;
      }

      // Standard JSON update
      const dataToSend = normalizeForApiV3(collectionData);
      const response = await API_V3.patch(url, dataToSend, {
        headers: { "Content-Type": "application/merge-patch+json" },
      });

      commit("SET_MERCHANDISING_COLLECTIONS_ERROR", null);
      return response.data;
    } catch (error) {
      console.error("Error updating collection:", error);
      commit(
        "SET_MERCHANDISING_COLLECTIONS_ERROR",
        error.message || "Failed to update collection"
      );
      throw error;
    } finally {
      if (!silent) {
        commit("SET_MERCHANDISING_COLLECTIONS_LOADING", false);
      }
    }
  },

  // Supprimer une collection (API v3)
  async deleteMerchandisingCollection({ commit }, { brandId, collectionId }) {
    try {
      commit("SET_MERCHANDISING_COLLECTIONS_LOADING", true);

      // URL basée sur le fichier call-api.md - Endpoint v3
      const url = `collection-pages/${collectionId}`;

      // Utilisation de l'API fetch directement pour un meilleur contrôle des en-têtes
      const response = await fetch(
        `${process.env.VUE_APP_API}/api/v3/private/${url}`,
        {
          method: "DELETE",
          headers: {
            "X-BRAND-ID": brandId,
            "Content-Type": "application/ld+json",
            Accept: "application/ld+json",
            Authorization: `Bearer ${store.state.user.access_token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to delete collection");
      }

      commit("SET_MERCHANDISING_COLLECTIONS_ERROR", null);
      return true;
    } catch (error) {
      console.error("Error deleting collection:", error);
      commit(
        "SET_MERCHANDISING_COLLECTIONS_ERROR",
        error.message || "Failed to delete collection"
      );
      throw error;
    } finally {
      commit("SET_MERCHANDISING_COLLECTIONS_LOADING", false);
    }
  },

  // Télécharger une photo de couverture pour une collection (API v2)
  async uploadMerchandisingCollectionCover(
    { commit },
    { brandId, collectionId, file }
  ) {
    try {
      commit("SET_MERCHANDISING_COLLECTIONS_LOADING", true);

      // Vérifications
      if (!file || !(file instanceof File)) {
        throw new Error("Fichier invalide");
      }

      // Vérifications
      if (!file || !(file instanceof File)) {
        throw new Error("Fichier invalide");
      }

      const formData = new FormData();
      formData.append("file", file, file.name);

      // URL corrigée
      const url = `${process.env.VUE_APP_API}/api/v2/admin/collection-pages/${collectionId}/photo-cover`;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "X-Brand-ID": brandId,
          Authorization: `Bearer ${store.state.user.access_token}`,
        },
        body: formData,
      });

      // 🆕 Améliorer la gestion des réponses
      let responseData;
      const contentType = response.headers.get("content-type");

      if (contentType && contentType.includes("application/json")) {
        // Réponse JSON
        try {
          responseData = await response.json();
        } catch (jsonError) {
          console.error("[Vuex] Erreur parsing JSON:", jsonError);
          responseData = { message: "Réponse JSON invalide" };
        }
      } else {
        // Réponse text/html ou autre
        responseData = await response.text();

        // Si c'est du HTML d'erreur, essayer d'extraire un message utile
        if (responseData.includes("<")) {
          // Essayer d'extraire le message d'erreur du HTML
          const titleMatch = responseData.match(/<title>(.*?)<\/title>/i);
          const h1Match = responseData.match(/<h1>(.*?)<\/h1>/i);
          const messageMatch = responseData.match(
            /message['":\s]*([^'",\n\r]*)/i
          );

          const extractedMessage =
            titleMatch?.[1] ||
            h1Match?.[1] ||
            messageMatch?.[1] ||
            "Erreur serveur";
          responseData = { message: extractedMessage };
        } else {
          // Si c'est du texte brut
          responseData = { message: responseData || "Erreur inconnue" };
        }
      }

      if (!response.ok) {
        const errorMessage =
          typeof responseData === "object"
            ? responseData.message || `HTTP ${response.status}`
            : responseData;

        console.error("[Vuex] Erreur serveur:", errorMessage);
        throw new Error(errorMessage);
      }

      // Gérer spécifiquement les réponses 204 (No Content)
      if (response.status === 204) {
        return { success: true, message: "Image uploadée avec succès" };
      }

      return responseData;
    } catch (error) {
      commit(
        "SET_MERCHANDISING_COLLECTIONS_ERROR",
        error.message || "Failed to upload collection cover"
      );
      throw error;
    } finally {
      commit("SET_MERCHANDISING_COLLECTIONS_LOADING", false);
    }
  },

  // Supprimer plusieurs collections (Utilise deleteMerchandisingCollection)
  async deleteMultipleMerchandisingCollections({ dispatch }, items) {
    const deletePromises = items.map((item) => {
      return dispatch("deleteMerchandisingCollection", {
        brandId: item.brandId,
        collectionId: item.id,
      });
    });

    return Promise.all(deletePromises);
  },

  // Action pour vérifier si un ID ou titre de collection existe déjà
  async checkCollectionExists({ commit }, { brandId, idCollection, title }) {
    commit("SET_MERCHANDISING_COLLECTIONS_LOADING", true);
    try {
      const url = `collection-pages/brand/${brandId}`;
      const response = await API.get(url, {
        headers: { "X-BRAND-ID": brandId },
        params: {
          locale: getClientLocale(brandId),
        },
      });
      // API v2 : structure { items: [...], pagination: {...} }
      const rawList =
        response.data.items ||
        (Array.isArray(response.data) ? response.data : []);

      // 🆕 API v2 : pas de normalisation, utiliser les données directement
      const idExists = rawList.some((c) => {
        // API v2 : collectionId est une propriété directe
        return (
          c.collectionId &&
          c.collectionId.toLowerCase() === idCollection.trim().toLowerCase()
        );
      });

      const titleExists = rawList.some((c) => {
        // API v2 : title est une propriété directe
        return c.title && c.title.toLowerCase() === title.trim().toLowerCase();
      });

      return { idExists, titleExists };
    } catch (err) {
      console.error("Erreur checkCollectionExists:", err);
      // En cas d’erreur, on renvoie false pour ne pas bloquer à tort
      return { idExists: false, titleExists: false };
    } finally {
      commit("SET_MERCHANDISING_COLLECTIONS_LOADING", false);
    }
  },
  //  Récupérer les filtres disponibles pour les articles (API v2)
  async fetchArticlesFilters({ commit }, brandId) {
    try {
      commit("SET_MERCHANDISING_COLLECTIONS_LOADING", true);

      // Nouvel endpoint unifié pour récupérer tous les filtres
      const url = `brand/${brandId}/articles_filters`;

      const response = await API.get(url, {
        headers: {
          "X-BRAND-ID": brandId,
          "Content-Type": "application/ld+json",
        },
        // params: {
        //   locale: getClientLocale(brandId),
        // },
      });

      commit("SET_MERCHANDISING_COLLECTIONS_ERROR", null);
      return response.data;
    } catch (error) {
      console.error("Error fetching articles filters:", error);
      commit(
        "SET_MERCHANDISING_COLLECTIONS_ERROR",
        error.message || "Failed to fetch articles filters"
      );
      return null;
    } finally {
      commit("SET_MERCHANDISING_COLLECTIONS_LOADING", false);
    }
  },

  // DEPRECATED: Utiliser fetchArticlesFilters à la place
  // Récupérer tous les champs disponibles pour filtrer les collections par marque
  async fetchCollectionFieldsByBrand({ commit }, brandId) {
    try {
      commit("SET_MERCHANDISING_COLLECTIONS_LOADING", true);

      // URL basée sur le fichier call-api.md - Endpoint v2
      const url = `collection-pages/brand/${brandId}/all-fields-by-brand`;

      const response = await API.get(url, {
        headers: {
          "X-BRAND-ID": brandId,
          "Content-Type": "application/ld+json",
        },
        params: {
          locale: getClientLocale(brandId),
        },
      });

      commit("SET_MERCHANDISING_COLLECTIONS_ERROR", null);
      return response.data;
    } catch (error) {
      console.error("Error fetching collection fields:", error);
      commit(
        "SET_MERCHANDISING_COLLECTIONS_ERROR",
        error.message || "Failed to fetch collection fields"
      );
      return null;
    } finally {
      commit("SET_MERCHANDISING_COLLECTIONS_LOADING", false);
    }
  },

  // Récupérer l'URL d'un article pour l'ouvrir sur le site (API v2)
  async fetchArticleUrl({ commit }, { brandId, tiId }) {
    try {
      // URL de l'API pour récupérer l'URL de l'article
      const url = `brand/${brandId}/collection/trade-in/${tiId}/article-url`;

      const response = await API.get(url, {
        headers: {
          "X-BRAND-ID": brandId,
        },
        params: {
          locale: getClientLocale(brandId),
        },
      });

      return response.data;
    } catch (error) {
      commit(
        "SET_MERCHANDISING_COLLECTIONS_ERROR",
        error.message || "Failed to fetch article URL"
      );
      throw error;
    }
  },

  // Actions pour la modale de construction de collection
  showBuildingModal({ commit }, message) {
    commit("SET_BUILDING_COLLECTION_MESSAGE", message);
    commit("SET_BUILDING_COLLECTION_MODAL", true);
  },
  hideBuildingModal({ commit }) {
    commit("SET_BUILDING_COLLECTION_MODAL", false);
    commit("SET_BUILDING_COLLECTION_MESSAGE", "");
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
